import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonBase,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Checkbox,
  Chip,
  IconButton,
  Collapse,
  Skeleton,
  Stack,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Download,
  Shield,
  AlertCircle,
  Heart,
} from 'lucide-react';
import { NameEmailAvatar } from '../../../../../../../ui/components/Common/NameEmailAvatar';
import {
  WellnessAssessmentOverview,
  WellnessCandidateResult,
  WellnessDASS21Detail,
  WellnessGAD7Detail,
  WellnessPHQ9Detail,
  WellnessSummaryResult,
} from '../../../../../../../utils/types/wellness_types';
import {
  RISK_BADGE_CLASS,
  SEVERITY_BADGE_CLASS,
  SEVERITY_BAND_COLOR_CLASS,
  SEVERITY_BAND_COLOR_MAP,
} from '../../../Constants';

// ─── Static scoring data ───────────────────────────────────────────────────────

interface RangeSegment {
  label: string;
  flex: number;
  color: string;
}

const D21_DEP_RANGES: RangeSegment[] = [
  { label: 'Normal', flex: 5, color: '#34d399' },
  { label: 'Mild', flex: 2, color: '#facc15' },
  { label: 'Moderate', flex: 4, color: '#fbbf24' },
  { label: 'Severe', flex: 3, color: '#ef4444' },
  { label: 'Ext. Severe', flex: 8, color: '#be123c' },
];
const D21_ANX_RANGES: RangeSegment[] = [
  { label: 'Normal', flex: 4, color: '#34d399' },
  { label: 'Mild', flex: 1, color: '#facc15' },
  { label: 'Moderate', flex: 3, color: '#fbbf24' },
  { label: 'Severe', flex: 2, color: '#ef4444' },
  { label: 'Ext. Severe', flex: 12, color: '#be123c' },
];
const D21_STR_RANGES: RangeSegment[] = [
  { label: 'Normal', flex: 8, color: '#34d399' },
  { label: 'Mild', flex: 2, color: '#facc15' },
  { label: 'Moderate', flex: 3, color: '#fbbf24' },
  { label: 'Severe', flex: 4, color: '#ef4444' },
  { label: 'Ext. Severe', flex: 5, color: '#be123c' },
];
const GAD7_RANGES: RangeSegment[] = [
  { label: 'Minimal', flex: 5, color: '#34d399' },
  { label: 'Mild', flex: 5, color: '#facc15' },
  { label: 'Moderate', flex: 5, color: '#fbbf24' },
  { label: 'Severe', flex: 7, color: '#ef4444' },
];
const PHQ9_RANGES: RangeSegment[] = [
  { label: 'Minimal', flex: 5, color: '#34d399' },
  { label: 'Mild', flex: 5, color: '#facc15' },
  { label: 'Moderate', flex: 5, color: '#fbbf24' },
  { label: 'Mod. Severe', flex: 5, color: '#ef4444' },
  { label: 'Severe', flex: 8, color: '#be123c' },
];

const DASS21_INTERPRETATION: Record<string, Record<string, string>> = {
  Depression: {
    Normal: 'No significant depression symptoms present.',
    Mild: 'Some low mood and reduced interest in activities.',
    Moderate: 'Notable dysphoria and some functional impairment.',
    Severe: 'High levels of dysphoria, low mood and lack of interest.',
    'Extremely Severe': 'Severe depression requiring urgent clinical attention.',
  },
  Anxiety: {
    Normal: 'No significant anxiety symptoms present.',
    Mild: 'Some tension and physical signs of anxiety.',
    Moderate: 'Significant anxiety with physiological symptoms.',
    Severe: 'High levels of tension, worry and physiological arousal.',
    'Extremely Severe': 'Extreme anxiety requiring urgent clinical attention.',
  },
  Stress: {
    Normal: 'Effective stress management, able to relax well.',
    Mild: 'Slightly elevated stress with difficulty winding down.',
    Moderate: 'Significant stress affecting daily functioning.',
    Severe: 'High levels of chronic stress and difficulty relaxing.',
    'Extremely Severe': 'Extremely high stress requiring immediate support.',
  },
};

const DASS21_RANGE_LABELS: Record<string, Record<string, string>> = {
  Depression: { Normal: '0–4', Mild: '5–6', Moderate: '7–10', Severe: '11–13', 'Extremely Severe': '14+' },
  Anxiety: { Normal: '0–3', Mild: '4', Moderate: '5–7', Severe: '8–9', 'Extremely Severe': '10+' },
  Stress: { Normal: '0–7', Mild: '8–9', Moderate: '10–12', Severe: '13–16', 'Extremely Severe': '17+' },
};

const RESPONSE_LABELS: Record<number, string> = {
  0: 'Not at all',
  1: 'Several days',
  2: 'More than half the days',
  3: 'Nearly every day',
};

const RESPONSE_DOT: Record<number, string> = {
  0: 'bg-emerald-400',
  1: 'bg-yellow-400',
  2: 'bg-amber-400',
  3: 'bg-red-500',
};

const ICON_BG: Record<string, string> = { DASS21: '#ecfdf5', PHQ9: '#faf5ff', GAD7: '#eff6ff' };
const ICONS: Record<string, React.ReactNode> = {
  DASS21: <Shield size={20} color='#059669' />,
  PHQ9: <Heart size={20} color='#9333ea' />,
  GAD7: <AlertCircle size={20} color='#2563eb' />,
};

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  onPageChange: (p: number) => void;
}

const CollapsibleSection: React.FC<{
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, defaultOpen = false, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Box>
      <ButtonBase
        onClick={() => setIsOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          textAlign: 'left',
          mb: 1,
          py: 0.5,
        }}
      >
        <Typography variant='caption' sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
          {title}
        </Typography>
        <Box sx={{ color: 'text.secondary', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <ChevronDown size={16} />
        </Box>
      </ButtonBase>
      <Collapse in={isOpen}>
        {children}
      </Collapse>
    </Box>
  );
};

const OverviewCard: React.FC<{ data: WellnessAssessmentOverview }> = ({ data }) => {
  const total = data.bands.reduce((s, b) => s + b.count, 0);
  return (
    <Paper elevation={0} sx={{ flex: 1, minWidth: 220, borderRadius: 3, border: '1px solid', borderColor: 'grey.200', p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', width: 36, height: 36, alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: ICON_BG[data.type] ?? 'grey.50' }}>
          {ICONS[data.type]}
        </Box>
        <Box>
          <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'text.primary' }}>{data.displayName}</Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>{data.description}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, mb: 2 }}>
        <Box>
          <Typography variant='h4' sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>{data.completed}</Typography>
          <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary', mt: 0.5 }}>Completed</Typography>
        </Box>
        <Box>
          <Typography variant='h4' sx={{ fontWeight: 800, color: 'text.disabled', lineHeight: 1 }}>{data.pending}</Typography>
          <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.disabled', mt: 0.5 }}>Pending</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', height: 8, width: '100%', overflow: 'hidden', borderRadius: 4, gap: 0.5 }}>
        {total > 0 && data.bands.map((band) => (
          <Box key={band.label} sx={{ height: 8, borderRadius: '9999px', bgcolor: SEVERITY_BAND_COLOR_MAP[band.label] ?? '#cbd5e1', width: `${(band.count / total) * 100}%` }} />
        ))}
      </Box>
      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {data.bands.map((band) => (
          <Box key={band.label} component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>
            <Box component='span' sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEVERITY_BAND_COLOR_MAP[band.label] ?? '#cbd5e1' }} />
            {band.count} {band.label}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const SkeletonOverviewCard: React.FC = () => (
  <Paper elevation={0} sx={{ flex: 1, minWidth: 220, borderRadius: 3, border: '1px solid', borderColor: 'grey.200', p: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Skeleton variant='circular' width={36} height={36} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant='text' width={80} />
        <Skeleton variant='text' width={120} />
      </Box>
    </Box>
    <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
      <Skeleton variant='text' width={40} height={32} />
      <Skeleton variant='text' width={40} height={32} />
    </Box>
    <Skeleton variant='rounded' height={8} />
  </Paper>
);

const ScoreBar: React.FC<{ score: number; max: number; ranges: RangeSegment[] }> = ({ score, max, ranges }) => {
  const pct = Math.min(100, (score / max) * 100);
  return (
    <Box sx={{ position: 'relative', pt: 2.5, pb: 0.5 }}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: `${pct}%`,
          transform: 'translateX(-50%)',
          color: 'text.secondary',
          fontSize: '0.75rem',
          lineHeight: 1,
        }}
      >
        ▼
      </Box>
      <Box sx={{ display: 'flex', height: 12, width: '100%', overflow: 'hidden', borderRadius: 6 }}>
        {ranges.map((r, i) => (
          <Box key={i} sx={{ height: '100%', bgcolor: r.color, flex: r.flex }} />
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        {ranges.map((r, i) => (
          <Typography key={i} variant='caption' sx={{ fontSize: '0.625rem', color: 'text.secondary', lineHeight: 1 }}>
            {r.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

const ScoreCell: React.FC<{ score: number; max: number; severity: string }> = ({ score, max, severity }) => (
  <Box>
    <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>
      {score}<Typography component='span' variant='caption' sx={{ color: 'text.secondary' }}>/{max}</Typography>
    </Typography>
    <Chip label={severity} size='small' sx={{ mt: 0.5, height: 18, fontSize: '0.625rem', fontWeight: 600 }} />
  </Box>
);

const CandidatesResultsTable: React.FC<{
  results: WellnessCandidateResult[];
  isLoading: boolean;
  pagination: PaginationProps;
  onView: (r: WellnessCandidateResult) => void;
}> = ({ results, isLoading, pagination, onView }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { currentPage, totalPages, totalRecords, onPageChange } = pagination;

  const toggleAll = () =>
    setSelected((p) => (p.size === results.length ? new Set() : new Set(results.map((r) => r.candidateId))));
  const toggle = (id: string) =>
    setSelected((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const allSel = results.length > 0 && selected.size === results.length;

  return (
    <Stack spacing={2}>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 3 }}>
        <Table size='small'>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell padding='checkbox'>
                <Checkbox checked={allSel} onChange={toggleAll} size='small' />
              </TableCell>
              {['CANDIDATE', 'DASS-21', 'PHQ-9', 'GAD-7', 'OVERALL RISK', 'STATUS', 'COMPLETED ON'].map((h) => (
                <TableCell key={h} sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary' }}>
                  {h}
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell padding='checkbox'><Skeleton variant='rounded' width={18} height={18} /></TableCell>
                  <TableCell><Skeleton variant='text' width={120} /></TableCell>
                  <TableCell><Skeleton variant='text' width={60} /></TableCell>
                  <TableCell><Skeleton variant='text' width={60} /></TableCell>
                  <TableCell><Skeleton variant='text' width={60} /></TableCell>
                  <TableCell><Skeleton variant='rounded' width={70} height={20} /></TableCell>
                  <TableCell><Skeleton variant='text' width={70} /></TableCell>
                  <TableCell><Skeleton variant='text' width={80} /></TableCell>
                  <TableCell><Skeleton variant='rounded' width={50} height={24} /></TableCell>
                </TableRow>
              ))
            ) : results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>No results yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              results.map((r) => (
                <TableRow
                  key={r.candidateId}
                  hover
                  selected={selected.has(r.candidateId)}
                >
                  <TableCell padding='checkbox'>
                    <Checkbox checked={selected.has(r.candidateId)} onChange={() => toggle(r.candidateId)} size='small' />
                  </TableCell>
                  <TableCell>
                    <NameEmailAvatar name={r.name} email={r.email} avatar={undefined} avatarWidth='32px' avatarHeight='32px' nameFontSize='13px' emailFontSize='11px' />
                  </TableCell>
                  <TableCell>
                    {r.dass21 ? <ScoreCell score={r.dass21.totalScore} max={r.dass21.totalMax} severity={r.dass21.severity} /> : '—'}
                  </TableCell>
                  <TableCell>
                    {r.phq9 ? <ScoreCell score={r.phq9.totalScore} max={r.phq9.totalMax} severity={r.phq9.severity} /> : '—'}
                  </TableCell>
                  <TableCell>
                    {r.gad7 ? <ScoreCell score={r.gad7.totalScore} max={r.gad7.totalMax} severity={r.gad7.severity} /> : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip label={r.overallRisk} size='small' sx={{ fontWeight: 700, fontSize: '0.75rem' }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>{r.status}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>{r.completedOn ?? '—'}</Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <Button
                      size='small'
                      variant='outlined'
                      onClick={() => onView(r)}
                      sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!isLoading && totalRecords > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', color: 'text.secondary' }}>
          <Typography variant='caption'>{totalRecords} result{totalRecords !== 1 ? 's' : ''}</Typography>
          {totalPages > 1 && (
            <Stack direction='row' spacing={1}>
              <Button size='small' variant='outlined' onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} sx={{ borderRadius: 1.5, textTransform: 'none' }}>Prev</Button>
              <Typography variant='caption' sx={{ alignSelf: 'center', px: 1 }}>{currentPage} / {totalPages}</Typography>
              <Button size='small' variant='outlined' onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} sx={{ borderRadius: 1.5, textTransform: 'none' }}>Next</Button>
            </Stack>
          )}
        </Box>
      )}
    </Stack>
  );
};

const Dass21DetailCard: React.FC<{ d: WellnessDASS21Detail }> = ({ d }) => {
  const domains: Array<{ label: string; score: number; max: number; severity: string; ranges: RangeSegment[] }> = [
    { label: 'Depression', score: d.depressionScore, max: d.depressionMax, severity: d.depressionSeverity, ranges: D21_DEP_RANGES },
    { label: 'Anxiety', score: d.anxietyScore, max: d.anxietyMax, severity: d.anxietySeverity, ranges: D21_ANX_RANGES },
    { label: 'Stress', score: d.stressScore, max: d.stressMax, severity: d.stressSeverity, ranges: D21_STR_RANGES },
  ];

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 3, p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', width: 36, height: 36, alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: 'success.lighter', backgroundColor: '#ecfdf5' }}>
          <Shield size={20} color='#059669' />
        </Box>
        <Box>
          <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'text.primary' }}>DASS-21</Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>Depression, Anxiety &amp; Stress Scale · 21 items</Typography>
        </Box>
        <Box sx={{ ml: 'auto', textAlign: 'right' }}>
          <Typography variant='h6' sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>
            {d.totalScore}<Typography component='span' variant='caption' sx={{ color: 'text.secondary' }}>/{d.totalMax}</Typography>
          </Typography>
          <Chip label={d.severity} size='small' sx={{ mt: 0.5, height: 18, fontSize: '0.625rem', fontWeight: 600 }} />
        </Box>
      </Box>

      <Stack spacing={2} sx={{ mb: 2 }}>
        {domains.map((dom) => (
          <Paper key={dom.label} elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.100', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>{dom.label}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='body2' sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {dom.score}<Typography component='span' variant='caption' sx={{ color: 'text.secondary' }}>/{dom.max}</Typography>
                </Typography>
                <Chip label={dom.severity} size='small' sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }} />
              </Box>
            </Box>
            <ScoreBar score={dom.score} max={dom.max} ranges={dom.ranges} />
          </Paper>
        ))}
      </Stack>

      <CollapsibleSection title='Score Breakdown' defaultOpen>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.100', borderRadius: 2, mb: 2 }}>
          <Table size='small'>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                {['Domain', 'Score', 'Severity', 'Range', 'Interpretation'].map((h) => (
                  <TableCell key={h} sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {domains.map((dom) => (
                <TableRow key={dom.label}>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{dom.label}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{dom.score}/{dom.max}</TableCell>
                  <TableCell><Chip label={dom.severity} size='small' sx={{ height: 18, fontSize: '0.6rem' }} /></TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{DASS21_RANGE_LABELS[dom.label]?.[dom.severity] ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{DASS21_INTERPRETATION[dom.label]?.[dom.severity] ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CollapsibleSection>

      {d.questions.length > 0 && (
        <CollapsibleSection title='Question Responses'>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.100', borderRadius: 2 }}>
            <Table size='small'>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  {['#', 'Item', 'Domain', 'Score', 'Response'].map((h) => (
                    <TableCell key={h} sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {d.questions.map((q, i) => (
                  <TableRow key={q.questionId}>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.primary' }}>{q.text}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{q.domain ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{q.score}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{RESPONSE_LABELS[q.score] ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CollapsibleSection>
      )}
    </Paper>
  );
};

const Gad7DetailCard: React.FC<{ d: WellnessGAD7Detail }> = ({ d }) => (
  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 3, p: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{ display: 'flex', width: 36, height: 36, alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: '#eff6ff' }}>
        <AlertCircle size={20} color='#2563eb' />
      </Box>
      <Box>
        <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'text.primary' }}>GAD-7</Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>Generalised Anxiety Disorder · 7 items</Typography>
      </Box>
      <Box sx={{ ml: 'auto', textAlign: 'right' }}>
        <Typography variant='h6' sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>
          {d.totalScore}<Typography component='span' variant='caption' sx={{ color: 'text.secondary' }}>/{d.totalMax}</Typography>
        </Typography>
        <Chip label={d.severity} size='small' sx={{ mt: 0.5, height: 18, fontSize: '0.625rem', fontWeight: 600 }} />
      </Box>
    </Box>

    <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.100', borderRadius: 2, mb: 2 }}>
      <ScoreBar score={d.totalScore} max={d.totalMax} ranges={GAD7_RANGES} />
    </Paper>

    {d.questions.length > 0 && (
      <CollapsibleSection title='Item Breakdown'>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.100', borderRadius: 2 }}>
          <Table size='small'>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                {['#', 'Item', 'Score', 'Response'].map((h) => (
                  <TableCell key={h} sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {d.questions.map((q, i) => (
                <TableRow key={q.questionId}>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.primary' }}>{q.text}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{q.score}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{RESPONSE_LABELS[q.score] ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CollapsibleSection>
    )}
  </Paper>
);

const Phq9DetailCard: React.FC<{ d: WellnessPHQ9Detail }> = ({ d }) => (
  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 3, p: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{ display: 'flex', width: 36, height: 36, alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: '#faf5ff' }}>
        <Heart size={20} color='#9333ea' />
      </Box>
      <Box>
        <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'text.primary' }}>PHQ-9</Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>Patient Health Questionnaire · 9 items</Typography>
      </Box>
      <Box sx={{ ml: 'auto', textAlign: 'right' }}>
        <Typography variant='h6' sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>
          {d.totalScore}<Typography component='span' variant='caption' sx={{ color: 'text.secondary' }}>/{d.totalMax}</Typography>
        </Typography>
        <Chip label={d.severity} size='small' sx={{ mt: 0.5, height: 18, fontSize: '0.625rem', fontWeight: 600 }} />
      </Box>
    </Box>

    <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.100', borderRadius: 2, mb: 2 }}>
      <ScoreBar score={d.totalScore} max={d.totalMax} ranges={PHQ9_RANGES} />
    </Paper>

    {d.questions.length > 0 && (
      <CollapsibleSection title='Item Breakdown'>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.100', borderRadius: 2 }}>
          <Table size='small'>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                {['#', 'Item', 'Score', 'Response'].map((h) => (
                  <TableCell key={h} sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {d.questions.map((q, i) => (
                <TableRow key={q.questionId}>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.primary' }}>{q.text}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{q.score}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{RESPONSE_LABELS[q.score] ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CollapsibleSection>
    )}
  </Paper>
);

const CandidateDetailPage: React.FC<{
  candidate: WellnessCandidateResult;
  allCandidates: WellnessCandidateResult[];
  onBack: () => void;
  onSelectCandidate: (c: WellnessCandidateResult) => void;
}> = ({ candidate, allCandidates, onBack, onSelectCandidate }) => {
  const [detailTab, setDetailTab] = useState<'summary' | 'results'>('results');
  const idx = allCandidates.findIndex((c) => c.candidateId === candidate.candidateId);

  const handlePrev = () => {
    if (idx > 0) onSelectCandidate(allCandidates[idx - 1]);
  };

  const handleNext = () => {
    if (idx < allCandidates.length - 1) onSelectCandidate(allCandidates[idx + 1]);
  };

  const assessmentsCompleted = [
    candidate.dass21 ? 'DASS-21' : null,
    candidate.gad7 ? 'GAD-7' : null,
    candidate.phq9 ? 'PHQ-9' : null,
  ].filter(Boolean);

  const downloadExport = () => {
    const lines = [
      `Candidate Results – ${candidate.name}`,
      `Email: ${candidate.email}`,
      `Overall Risk: ${candidate.overallRisk}`,
      `Completed: ${candidate.completedOn ?? '—'}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${candidate.name.replace(/\s+/g, '_')}_results.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack spacing={3}>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
        <ButtonBase onClick={onBack} sx={{ fontSize: 'inherit', color: 'inherit', '&:hover': { color: 'primary.main' } }}>
          Wellness
        </ButtonBase>
        <Typography variant='caption'>›</Typography>
        <ButtonBase onClick={onBack} sx={{ fontSize: 'inherit', color: 'inherit', '&:hover': { color: 'primary.main' } }}>
          Screening Results
        </ButtonBase>
        <Typography variant='caption'>›</Typography>
        <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.primary' }}>
          Candidate Results
        </Typography>
      </Box>

      {/* Title + candidate nav */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Button
            size='small'
            onClick={onBack}
            startIcon={<ChevronLeft size={16} />}
            sx={{ mb: 1, textTransform: 'none', color: 'text.secondary', p: 0 }}
          >
            Back to results
          </Button>
          <Typography variant='h5' sx={{ fontWeight: 800, color: 'text.primary' }}>
            Candidate Results
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Individual wellness screening report
          </Typography>
        </Box>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <IconButton size='small' onClick={handlePrev} disabled={idx <= 0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
            <ChevronLeft size={16} />
          </IconButton>
          <NameEmailAvatar name={candidate.name} email={candidate.email} avatar={undefined} avatarWidth='36px' avatarHeight='36px' nameFontSize='13px' emailFontSize='11px' />
          <IconButton size='small' onClick={handleNext} disabled={idx >= allCandidates.length - 1} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
            <ChevronRight size={16} />
          </IconButton>
        </Stack>
      </Box>

      {/* Meta row */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2.5 }}>
            <Typography variant='caption' sx={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Completion Date
            </Typography>
            <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'text.primary' }}>
              {candidate.completedOn ?? 'Not completed'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2.5 }}>
            <Typography variant='caption' sx={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Assessments Completed
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {assessmentsCompleted.length === 0 ? (
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>None</Typography>
              ) : (
                assessmentsCompleted.map((a) => (
                  <Chip key={a!} label={a!} size='small' sx={{ height: 20, fontSize: '0.625rem', fontWeight: 700 }} />
                ))
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2.5 }}>
            <Typography variant='caption' sx={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Overall Wellness Risk
            </Typography>
            <Chip label={`${candidate.overallRisk} Risk`} size='small' sx={{ fontWeight: 800, fontSize: '0.75rem' }} />
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2.5 }}>
            <Typography variant='caption' sx={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Interpretation Guide
            </Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary', lineHeight: 1.2, display: 'block' }}>
              Scores reflect self-reported symptom severity at time of assessment.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Sub-tabs: Summary | Candidate Results */}
      <Tabs
        value={detailTab}
        onChange={(_, val) => setDetailTab(val)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label='Summary' value='summary' sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label='Candidate Results' value='results' sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      {/* Summary sub-tab */}
      {detailTab === 'summary' && (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 3, p: 3 }}>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
            Summary view shows the overall wellness picture for this candidate.
          </Typography>
          <Grid container spacing={2}>
            {candidate.dass21 && (
              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>DASS-21</Typography>
                  <Typography variant='h5' sx={{ fontWeight: 800, color: 'text.primary' }}>
                    {candidate.dass21.totalScore}<Typography component='span' variant='caption' sx={{ color: 'text.secondary' }}>/{candidate.dass21.totalMax}</Typography>
                  </Typography>
                  <Chip label={candidate.dass21.severity} size='small' sx={{ mt: 1, fontWeight: 600 }} />
                </Paper>
              </Grid>
            )}
            {candidate.gad7 && (
              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>GAD-7</Typography>
                  <Typography variant='h5' sx={{ fontWeight: 800, color: 'text.primary' }}>
                    {candidate.gad7.totalScore}<Typography component='span' variant='caption' sx={{ color: 'text.secondary' }}>/{candidate.gad7.totalMax}</Typography>
                  </Typography>
                  <Chip label={candidate.gad7.severity} size='small' sx={{ mt: 1, fontWeight: 600 }} />
                </Paper>
              </Grid>
            )}
            {candidate.phq9 && (
              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>PHQ-9</Typography>
                  <Typography variant='h5' sx={{ fontWeight: 800, color: 'text.primary' }}>
                    {candidate.phq9.totalScore}<Typography component='span' variant='caption' sx={{ color: 'text.secondary' }}>/{candidate.phq9.totalMax}</Typography>
                  </Typography>
                  <Chip label={candidate.phq9.severity} size='small' sx={{ mt: 1, fontWeight: 600 }} />
                </Paper>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Candidate Results sub-tab */}
      {detailTab === 'results' && (
        <Stack spacing={3}>
          <Grid container spacing={2}>
            {candidate.dass21 && <Grid item xs={12} xl={4}><Dass21DetailCard d={candidate.dass21} /></Grid>}
            {candidate.gad7 && <Grid item xs={12} xl={4}><Gad7DetailCard d={candidate.gad7} /></Grid>}
            {candidate.phq9 && <Grid item xs={12} xl={4}><Phq9DetailCard d={candidate.phq9} /></Grid>}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#fefce8', border: '1px solid #fef08a', borderRadius: 2.5 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, color: '#854d0e', mb: 1 }}>
                  Important Notes
                </Typography>
                <Stack spacing={0.5} component='ul' sx={{ pl: 2, m: 0, color: '#a16207', fontSize: '0.75rem' }}>
                  <li>This assessment is self-reported and should not replace clinical evaluation.</li>
                  <li>Results should be reviewed by a qualified mental health professional.</li>
                  <li>Scores may vary based on the candidate&apos;s state at the time of completion.</li>
                  <li>All data is handled in accordance with privacy regulations.</li>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 2.5 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, color: '#1e40af', mb: 1 }}>
                  Recommended Actions
                </Typography>
                {candidate.overallRisk === 'High' ? (
                  <Stack spacing={0.5} component='ul' sx={{ pl: 2, m: 0, color: '#1d4ed8', fontSize: '0.75rem' }}>
                    <li>Refer candidate to an Employee Assistance Programme (EAP) immediately.</li>
                    <li>Schedule a confidential conversation with HR or occupational health.</li>
                    <li>Consider role adjustments or flexible arrangements if proceeding.</li>
                    <li>Follow up with a qualified mental health professional within 48 hours.</li>
                  </Stack>
                ) : candidate.overallRisk === 'Moderate' ? (
                  <Stack spacing={0.5} component='ul' sx={{ pl: 2, m: 0, color: '#1d4ed8', fontSize: '0.75rem' }}>
                    <li>Offer access to counselling or EAP resources.</li>
                    <li>Monitor closely during onboarding and first 90 days.</li>
                    <li>Discuss support structures with candidate if they proceed.</li>
                  </Stack>
                ) : (
                  <Stack spacing={0.5} component='ul' sx={{ pl: 2, m: 0, color: '#1d4ed8', fontSize: '0.75rem' }}>
                    <li>No immediate action required.</li>
                    <li>Continue standard onboarding and wellbeing check-ins.</li>
                    <li>Encourage candidate to self-refer to EAP if needed in future.</li>
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant='contained'
              onClick={downloadExport}
              startIcon={<Download size={16} />}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3, py: 1 }}
            >
              Export Candidate Results
            </Button>
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

// ─── Main: WellnessScreeningPage ──────────────────────────────────────────────

interface WellnessScreeningPageProps {
  summary: WellnessSummaryResult | null;
  candidateResults: WellnessCandidateResult[];
  isLoading: boolean;
  pagination: PaginationProps;
}

type PageView = 'overview' | 'detail';
type OverviewTab = 'summary' | 'candidates';

const downloadSummary = (summary: WellnessSummaryResult) => {
  const lines = ['Assessment,Completed,Pending', ...summary.assessments.map((a) => `${a.displayName},${a.completed},${a.pending}`)];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'wellness_summary.csv';
  a.click();
  URL.revokeObjectURL(url);
};

export const WellnessScreeningPage: React.FC<WellnessScreeningPageProps> = ({
  summary,
  candidateResults,
  isLoading,
  pagination,
}) => {
  const [view, setView] = useState<PageView>('overview');
  const [overviewTab, setOverviewTab] = useState<OverviewTab>('summary');
  const [detailCandidate, setDetailCandidate] = useState<WellnessCandidateResult | null>(null);

  const handleView = (r: WellnessCandidateResult) => {
    setDetailCandidate(r);
    setView('detail');
  };

  const handleBack = () => {
    setView('overview');
    setDetailCandidate(null);
  };

  if (view === 'detail' && detailCandidate) {
    return (
      <CandidateDetailPage
        candidate={detailCandidate}
        allCandidates={candidateResults}
        onBack={handleBack}
        onSelectCandidate={setDetailCandidate}
      />
    );
  }

  return (
    <Stack spacing={3}>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
        <Typography variant='caption'>Wellness</Typography>
        <Typography variant='caption'>›</Typography>
        <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.primary' }}>
          Screening Results
        </Typography>
      </Box>

      {/* Header + export buttons */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 800, color: 'text.primary' }}>
            Wellness Screening
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Overview of candidate wellness screening results.
          </Typography>
        </Box>
        <Stack direction='row' spacing={1.5}>
          <Button
            variant='outlined'
            color='inherit'
            onClick={() => summary && downloadSummary(summary)}
            disabled={!summary}
            startIcon={<Download size={16} />}
            sx={{ borderRadius: 2, textTransform: 'none', height: 40 }}
          >
            Export Summary
          </Button>
          <Button
            variant='contained'
            onClick={() => summary && downloadSummary(summary)}
            disabled={!summary}
            startIcon={<Download size={16} />}
            sx={{ borderRadius: 2, textTransform: 'none', height: 40 }}
          >
            Export All Results
          </Button>
        </Stack>
      </Box>

      {/* Assessment overview cards */}
      <Box>
        <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
          Summary Overview
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => <SkeletonOverviewCard key={i} />)
          ) : summary ? (
            summary.assessments.map((a) => <OverviewCard key={a.type} data={a} />)
          ) : (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>No data available yet.</Typography>
          )}
        </Box>
      </Box>

      {/* Sub-tabs: Summary | Candidate Results */}
      <Tabs
        value={overviewTab}
        onChange={(_, val) => setOverviewTab(val)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label='Summary' value='summary' sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label='Candidate Results' value='candidates' sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      {/* Summary tab content */}
      {overviewTab === 'summary' && (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 3, p: 3 }}>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
            High-level overview of assessment completion and severity distribution.
          </Typography>
          {summary ? (
            <Stack spacing={2}>
              {summary.assessments.map((a) => {
                const total = a.bands.reduce((s, b) => s + b.count, 0);
                return (
                  <Box key={a.type} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant='body2' sx={{ width: 80, fontWeight: 700, color: 'text.primary' }}>
                      {a.displayName}
                    </Typography>
                    <Box sx={{ flex: 1, display: 'flex', height: 16, overflow: 'hidden', borderRadius: 8, gap: 0.5 }}>
                      {total > 0 &&
                        a.bands.map((band) => (
                          <Box
                            key={band.label}
                            sx={{ height: 16, borderRadius: '9999px', bgcolor: SEVERITY_BAND_COLOR_MAP[band.label] ?? '#cbd5e1', width: `${(band.count / total) * 100}%` }}
                            title={`${band.label}: ${band.count}`}
                          />
                        ))}
                    </Box>
                    <Typography variant='caption' sx={{ width: 100, textAlign: 'right', color: 'text.secondary' }}>
                      {a.completed} completed
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>No summary data available yet.</Typography>
          )}
        </Paper>
      )}

      {/* Candidate Results tab content */}
      {overviewTab === 'candidates' && (
        <CandidatesResultsTable
          results={candidateResults}
          isLoading={isLoading}
          pagination={pagination}
          onView={handleView}
        />
      )}
    </Stack>
  );
};
